import { QueryKey, UseQueryOptions, UseQueryResult, useQuery as useReactQuery, hashKey } from "@tanstack/react-query";
import React, { useContext } from "react";

import { QueryTrackingContext } from "@/stores/QueryTrackingContext";
import { throwExpression } from "@/utils/util";

/**
 * Hook to cal `useQuery` from react-query and register it with the `QueryTrackingContext`. Using this hook allows the
 * `QueryTrackingContext` to track all queries that are currently being used in a rendered component.
 */
export function useTrackedQuery<
  TQueryFnData = unknown,
  TError extends Error = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  // Hash the query key to a string
  const hashedQueryKey = hashKey(options.queryKey);

  // Get the context where we registered the query
  const { registerQuery, unregisterQuery } =
    useContext(QueryTrackingContext) ?? throwExpression("useTrackedQuery must be used within a QueryTrackingProvider");

  // Initiate the query using react-query
  const result = useReactQuery(options);

  // Register the query with the context and register a callback to unregister the query when the component unmounts
  // NOTE: `result` is not included in the dependency array for this `useEffect` call, because We don't want to re-run
  // the effect when the query result changes. Checking the hashed query key is sufficient for checking equality of the
  // query. Re-running for changes to the query result object would result in an infinite loop
  React.useEffect(() => {
    registerQuery(hashedQueryKey, result);
    return () => {
      unregisterQuery(hashedQueryKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above
  }, [registerQuery, unregisterQuery, hashedQueryKey]);

  // Return the query result
  return result;
}
